import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, Package, ShoppingCart,
  Calendar, BarChart3, RefreshCw, ChevronRight, Clock, Target,
  ArrowUp, ArrowDown, Loader2, FileText, CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par urgence
const URGENCY_COLORS = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white'
};

const URGENCY_LABELS = {
  critical: 'Critique',
  high: 'Haute',
  medium: 'Moyenne',
  low: 'Basse'
};

// Composant StatCard
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'emerald' }) => (
  <Card className={`border-l-4 border-l-${color}-500`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center mt-2 text-xs">
          {trend > 0 ? (
            <TrendingUp className="w-3 h-3 mr-1 text-green-500" />
          ) : trend < 0 ? (
            <TrendingDown className="w-3 h-3 mr-1 text-red-500" />
          ) : (
            <Minus className="w-3 h-3 mr-1 text-gray-500" />
          )}
          <span>{trend > 0 ? '+' : ''}{trend}% vs période précédente</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Composant DayPerformanceBar
const DayPerformanceBar = ({ day }) => {
  const maxFactor = 1.5;
  const percentage = Math.min((day.weight_factor / maxFactor) * 100, 100);
  const color = day.performance === 'fort' ? 'bg-emerald-500' : 
                day.performance === 'faible' ? 'bg-red-400' : 'bg-blue-500';
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium">{day.day_fr}</span>
        <span className="text-muted-foreground">
          {day.avg_revenue?.toLocaleString('fr-FR')} F
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Facteur: {day.weight_factor}x</span>
        <span>{day.total_orders} commandes</span>
      </div>
    </div>
  );
};

// Composant RecommendationCard
const RecommendationCard = ({ item, onGenerateList }) => {
  const TrendIcon = item.trend === 'hausse' ? ArrowUp : 
                    item.trend === 'baisse' ? ArrowDown : Minus;
  
  return (
    <div className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium truncate">{item.product_name}</h4>
            <Badge className={URGENCY_COLORS[item.urgency]}>
              {URGENCY_LABELS[item.urgency]}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{item.category}</p>
          <p className="text-xs text-muted-foreground mt-1">{item.reason}</p>
        </div>
        
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-emerald-600">
            +{item.recommended_quantity} {item.unit}
          </p>
          <p className="text-sm text-muted-foreground">
            ~{item.estimated_cost?.toLocaleString('fr-FR')} F
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t text-xs">
        <div>
          <p className="text-muted-foreground">Stock actuel</p>
          <p className="font-medium">{item.current_stock} {item.unit}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Consommation/j</p>
          <p className="font-medium">{item.daily_avg_consumption} {item.unit}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Prévision {item.days_ahead}j</p>
          <p className="font-medium flex items-center gap-1">
            {item.predicted_consumption} {item.unit}
            {item.trend && (
              <TrendIcon className={`w-3 h-3 ${
                item.trend === 'hausse' ? 'text-red-500' : 
                item.trend === 'baisse' ? 'text-green-500' : 'text-gray-500'
              }`} />
            )}
          </p>
        </div>
      </div>
      
      {item.confidence && (
        <div className="mt-2 pt-2 border-t">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Confiance:</span>
            <Badge variant="outline" className={
              item.confidence === 'high' ? 'border-green-500 text-green-600' :
              item.confidence === 'medium' ? 'border-yellow-500 text-yellow-600' :
              'border-red-500 text-red-600'
            }>
              {item.confidence === 'high' ? 'Haute' : 
               item.confidence === 'medium' ? 'Moyenne' : 'Faible'}
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ForecastPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [daysAhead, setDaysAhead] = useState('7');
  
  const [dashboard, setDashboard] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [dayAnalysis, setDayAnalysis] = useState(null);
  const [trends, setTrends] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashboardRes, recoRes, dayRes, trendsRes] = await Promise.all([
        fetch(`${API}/api/forecast/dashboard`, { headers }),
        fetch(`${API}/api/forecast/recommendations?days_ahead=${daysAhead}&advanced=true`, { headers }),
        fetch(`${API}/api/forecast/day-analysis`, { headers }),
        fetch(`${API}/api/forecast/trends`, { headers })
      ]);
      
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (recoRes.ok) setRecommendations(await recoRes.json());
      if (dayRes.ok) setDayAnalysis(await dayRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      toast.error('Erreur lors du chargement des prévisions');
    } finally {
      setLoading(false);
    }
  }, [token, daysAhead]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateShoppingList = async () => {
    setGenerating(true);
    try {
      const response = await fetch(
        `${API}/api/forecast/generate-shopping-list?days_ahead=${daysAhead}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (response.ok && data.list_id) {
        toast.success(`Liste ${data.list_number} créée avec ${data.total_products} produits`);
      } else {
        toast.info(data.message || 'Aucune liste générée');
      }
    } catch (error) {
      console.error('Error generating list:', error);
      toast.error('Erreur lors de la génération de la liste');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="forecast-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-emerald-600" />
            Prévision des Achats
          </h1>
          <p className="text-muted-foreground">
            Anticipez vos besoins en stock avec l'analyse prédictive
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={daysAhead} onValueChange={setDaysAhead}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 jours</SelectItem>
              <SelectItem value="14">14 jours</SelectItem>
              <SelectItem value="30">30 jours</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      {dashboard && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Produits à risque"
            value={dashboard.statistics.products_at_risk}
            subtitle={`sur ${dashboard.statistics.total_products} produits`}
            icon={AlertTriangle}
            color="orange"
          />
          <StatCard
            title="Critiques"
            value={dashboard.statistics.critical_count}
            subtitle="Rupture imminente"
            icon={Package}
            color="red"
          />
          <StatCard
            title="CA Moyen/jour"
            value={`${dashboard.statistics.avg_daily_revenue?.toLocaleString('fr-FR')} F`}
            subtitle="30 derniers jours"
            icon={TrendingUp}
            color="emerald"
          />
          <StatCard
            title="Commandes"
            value={dashboard.statistics.total_orders_30d}
            subtitle="30 derniers jours"
            icon={ShoppingCart}
            color="blue"
          />
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Tableau de bord</span>
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline">Recommandations</span>
          </TabsTrigger>
          <TabsTrigger value="analysis" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Analyse</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Top Products */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Top Produits Vendus
                </CardTitle>
                <CardDescription>30 derniers jours</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.top_selling_products?.slice(0, 8).map((product, idx) => (
                    <div key={product.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          idx < 3 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="text-sm font-medium truncate max-w-[150px]">
                          {product.name}
                        </span>
                      </div>
                      <Badge variant="outline">{product.quantity} vendus</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Risk Products */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Produits à Risque
                </CardTitle>
                <CardDescription>Stock critique ou bas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboard?.risk_products?.slice(0, 8).map((product) => (
                    <div key={product.id} className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Stock: {product.current_stock} {product.unit}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={URGENCY_COLORS[product.urgency]}>
                          {product.days_until_stockout < 999 
                            ? `${product.days_until_stockout}j` 
                            : '-'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          {recommendations && (
            <>
              {/* Summary */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="font-medium">
                        {recommendations.summary.total_products} produits à commander
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Coût estimé: {recommendations.summary.total_estimated_cost?.toLocaleString('fr-FR')} F CFA
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-red-500 text-red-600">
                        {recommendations.summary.critical} critiques
                      </Badge>
                      <Badge variant="outline" className="border-orange-500 text-orange-600">
                        {recommendations.summary.high} hautes
                      </Badge>
                      <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                        {recommendations.summary.medium} moyennes
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button 
                      onClick={handleGenerateShoppingList}
                      disabled={generating || recommendations.summary.total_products === 0}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {generating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      Générer liste d'achats
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recommendations List */}
              <div className="space-y-3">
                {recommendations.recommendations?.map((item) => (
                  <RecommendationCard 
                    key={item.product_id} 
                    item={item}
                  />
                ))}
                
                {recommendations.recommendations?.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                      <h3 className="font-medium">Aucun achat nécessaire</h3>
                      <p className="text-sm text-muted-foreground">
                        Tous vos stocks sont suffisants pour les {daysAhead} prochains jours
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Day Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Performance par Jour
                </CardTitle>
                <CardDescription>
                  Facteurs de vente par jour de la semaine
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {dayAnalysis?.days?.map((day) => (
                  <DayPerformanceBar key={day.day_en} day={day} />
                ))}
                
                {dayAnalysis && (
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Meilleur jour:</span>
                      <span className="font-medium text-emerald-600">
                        {dayAnalysis.best_day.day} ({dayAnalysis.best_day.factor}x)
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Jour le plus faible:</span>
                      <span className="font-medium text-red-600">
                        {dayAnalysis.worst_day.day} ({dayAnalysis.worst_day.factor}x)
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {trends?.summary?.trend_direction === 'up' ? (
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  ) : trends?.summary?.trend_direction === 'down' ? (
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  ) : (
                    <Minus className="w-5 h-5 text-gray-600" />
                  )}
                  Tendances de Ventes
                </CardTitle>
                <CardDescription>
                  Évolution sur {trends?.period_days || 30} jours
                </CardDescription>
              </CardHeader>
              <CardContent>
                {trends && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-muted-foreground">CA moyen/jour</p>
                        <p className="text-xl font-bold">
                          {trends.summary.avg_daily_revenue?.toLocaleString('fr-FR')} F
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-muted-foreground">Commandes/jour</p>
                        <p className="text-xl font-bold">
                          {trends.summary.avg_daily_orders}
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Tendance</span>
                        <Badge className={
                          trends.summary.trend_direction === 'up' 
                            ? 'bg-emerald-500' 
                            : trends.summary.trend_direction === 'down'
                            ? 'bg-red-500'
                            : 'bg-gray-500'
                        }>
                          {trends.summary.trend_direction === 'up' ? 'Hausse' :
                           trends.summary.trend_direction === 'down' ? 'Baisse' : 'Stable'}
                          {' '}
                          {trends.summary.trend_percentage > 0 ? '+' : ''}
                          {trends.summary.trend_percentage}%
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Mini chart representation */}
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Derniers jours</p>
                      <div className="flex items-end gap-1 h-16">
                        {trends.daily_data?.slice(-14).map((day, idx) => {
                          const maxRevenue = Math.max(...trends.daily_data.slice(-14).map(d => d.revenue || 1));
                          const height = ((day.revenue || 0) / maxRevenue) * 100;
                          return (
                            <div
                              key={day.date}
                              className="flex-1 bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                              style={{ height: `${Math.max(height, 5)}%` }}
                              title={`${day.date}: ${day.revenue?.toLocaleString('fr-FR')} F`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
